import { formatDate } from '@/lib/format';
import type { TimetableRange } from '@/types/timetableAdmin';

/**
 * Reading a section's dated timetables: which one a week falls under, and how
 * to say its dates. Kept apart from the page so the bar, the dialogs and the
 * empty state all describe a timetable's dates the same way (CLAUDE.md §3).
 *
 * Dates are `YYYY-MM-DD`, which compare correctly as strings.
 */

/** "1 Apr 2026 – 31 Oct 2026", or "From 1 Nov 2026" when it runs to the session's end. */
export function describeTimetableRange(
  range: Pick<TimetableRange, 'effectiveFrom' | 'effectiveTo'>,
) {
  return range.effectiveTo
    ? `${formatDate(range.effectiveFrom)} – ${formatDate(range.effectiveTo)}`
    : `From ${formatDate(range.effectiveFrom)}`;
}

/** Whether a timetable applies on at least one day from `from` to `to`. */
function overlaps(range: TimetableRange, from: string, to: string) {
  return range.effectiveFrom <= to && (range.effectiveTo === null || range.effectiveTo >= from);
}

/**
 * The timetable the week on screen is built from.
 *
 * The one in force on the week's first day, or failing that the first to start
 * during the week — a timetable that begins on a Wednesday is still the one the
 * office wants to see that week. Null when no timetable touches the week at all.
 */
export function timetableForWeek<T extends TimetableRange>(timetables: T[], weekDates: string[]) {
  const first = weekDates[0];
  const last = weekDates[weekDates.length - 1];
  const touching = timetables.filter((range) => overlaps(range, first, last));

  return (
    touching.find((range) => range.effectiveFrom <= first) ??
    [...touching].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))[0] ??
    null
  );
}
